import { FilterValues } from '../components/MonitoringReport/MonitoringReport'
import { monitoringReportTranslations as translations } from 'locale/monitoringReports'
import { getQueryParams } from 'utils/getQueryParams'
import dayjs from 'dayjs'

type QueryParams = {
  resident: string
  to?: string
  from?: string
  items?: string[]
  loggedBy?: string[]
  categories?: string[]
}

export const generateQueryString = (params: QueryParams): string => {
  const parameters: string[] = []

  if (params.resident) {
    parameters.push(`resident=${params.resident}`)
  }

  if (params.items) {
    params.items?.forEach((item) => parameters.push(`items=${item}`))
  }

  if (params.from) {
    parameters.push(`from=${params.from}`)
  }

  if (params.to) {
    parameters.push(`to=${params.to}`)
  }

  if (params?.loggedBy) {
    params?.loggedBy?.forEach((loggedByItem) =>
      parameters.push(`loggedBy=${loggedByItem}`),
    )
  }

  if (params.categories) {
    params?.categories?.forEach((category) =>
      parameters.push(`category=${category}`),
    )
  }

  return parameters.length > 0 ? `?${parameters.join('&')}` : ''
}

export const generateApiUrl = (
  filterParams: FilterValues,
  reportingId: string,
  aggregation: string,
) => {
  const { residents, rangePicker, loggedBy, categories, categoryItems } =
    filterParams

  const queryStr = getQueryParams({
    timezone: String(dayjs().utcOffset()),
    loggedBy,
    residents,
    categories,
    categoryItems,
    from: rangePicker?.[0]?.toISOString(),
    to: rangePicker?.[1]?.toISOString(),
  })

  return `/api/list/logs/reports/${reportingId}/${aggregation}?${queryStr}`
}

export const generateApiUrlFluidsOverview = (filterParams: FilterValues) => {
  const { rangePicker } = filterParams

  const queryStr = getQueryParams({
    from: rangePicker?.[0]?.format('YYYY-MM-DD'),
    to: rangePicker?.[1]?.format('YYYY-MM-DD'),
  })

  return `/api/care-plans/fluid-intake-targets?${queryStr}`
}

export const popoverReportMappings = {
  fluids_in: {
    title: translations.FLUIDS_POPOVER_TITLE,
    description: translations.FLUIDS_POPOVER_DESCRIPTION,
  },
  meal: {
    title: translations.MEALS_POPOVER_TITLE,
    description: translations.MEALS_POPOVER_DESCRIPTION,
  },
  bowel_movement: {
    title: translations.BOWEL_MOVEMENT_POPOVER_TITLE,
    description: translations.BOWEL_MOVEMENT_POPOVER_DESCRIPTION,
  },
  turns: {
    title: translations.TURNS_POPOVER_TITLE,
    description: translations.TURNS_POPOVER_DESCRIPTION,
  },
  weight: {
    title: translations.WEIGHT_POPOVER_TITLE,
    description: translations.WEIGHT_POPOVER_DESCRIPTION,
  },
  temperature: {
    title: translations.TEMPERATURE_POPOVER_TITLE,
    description: translations.TEMPERATURE_POPOVER_DESCRIPTION,
  },
  activity_social: {
    title: translations.SOCIAL_POPOVER_TITLE,
    description: translations.SOCIAL_POPOVER_DESCRIPTION,
  },
  stool: {
    title: translations.STOOL_POPOVER_TITLE,
    description: translations.STOOL_POPOVER_DESCRIPTION,
  },
  mood: {
    title: translations.MOOD_POPOVER_TITLE,
    description: translations.MOOD_POPOVER_DESCRIPTION,
  },
  all: {
    title: translations.ALL_POPOVER_TITLE,
    description: translations.ALL_POPOVER_DESCRIPTION,
  },
}

type ReportMapping = {
  title: string
  reportingId: string
  description: string
  aggregation: 'sum' | 'count' | 'avg'
}

type ReportMappingsType = {
  [key: string]: ReportMapping
}

export const reportMappings: ReportMappingsType = {
  fluids: {
    title: translations.FLUIDS_TITLE,
    reportingId: 'fluids_in',
    description: translations.FLUIDS_DESCRIPTION,
    aggregation: 'sum',
  },
  turns: {
    title: translations.TURNS_REPORT,
    reportingId: 'turns',
    description: translations.TURNS_REPORT_DESCRIPTION,
    aggregation: 'count',
  },
  meals: {
    title: translations.MEALS_REPORT,
    reportingId: 'meal',
    description: translations.MEALS_REPORT_DESCRIPTION,
    aggregation: 'sum',
  },
  bowel: {
    title: translations.BOWEL_MOVEMENT_REPORT,
    reportingId: 'bowel_movement',
    description: translations.BOWEL_MOVEMENT_REPORT_DESCRIPTION,
    aggregation: 'count',
  },
  mood: {
    title: translations.MOOD_REPORT,
    reportingId: 'mood',
    description: translations.MOOD_REPORT_DESCRIPTION,
    aggregation: 'avg',
  },
  temperature: {
    title: translations.TEMPERATURE_REPORT,
    reportingId: 'temperature',
    description: translations.TEMPERATURE_REPORT_DESCRIPTION,
    aggregation: 'avg',
  },
  activity: {
    title: translations.ACTIVITY_SOCIAL_REPORT,
    reportingId: 'activity_social',
    description: translations.ACTIVITY_SOCIAL_REPORT_DESCRIPTION,
    aggregation: 'count',
  },
  stool: {
    title: translations.STOOL_REPORT,
    reportingId: 'stool',
    description: translations.STOOL_REPORT_DESCRIPTION,
    aggregation: 'count',
  },
  wash: {
    title: translations.WASH_REPORT,
    reportingId: 'wash',
    description: translations.WASH_REPORT_DESCRIPTION,
    aggregation: 'count',
  },
  weight: {
    title: translations.WEIGHT_REPORT,
    reportingId: 'weight',
    description: translations.WEIGHT_REPORT_DESCRIPTION,
    aggregation: 'avg',
  },
  custom: {
    title: translations.ALL_LOGS_REPORT,
    reportingId: 'all',
    description: translations.ALL_LOGS_REPORT_DESCRIPTION,
    aggregation: 'count',
  },
}

export const getReportPath = (key: string): string => {
  const exceptionMap: { [key: string]: string } = {
    fluids_in: 'fluids',
    food: 'meal',
    activity: 'activity_social',
    bowel: '',
    all: '',
  }

  return exceptionMap[key] !== undefined ? exceptionMap[key] : key
}

const endingWithZeroRegEx = /0(?!.*0)/

export const formatData = (num: number) => {
  if (num > 1000) {
    return num.toLocaleString('en-US')
  }
  if (num % 1 === 0) {
    return num.toString()
  }
  if (num < 1) {
    return num.toFixed(2)
  }
  return num.toFixed(2).replace(endingWithZeroRegEx, '')
}
